import { useState, useEffect, useMemo } from 'react';
import { logger } from '@/features/help/utils/logger';
import { signerApi } from '@/features/application/api/signer.api';
import { Signer } from '../types/signers';
import { validateEmail } from '../utils/validation.utils';

interface UseSignersModalProps {
  isOpen: boolean;
  userShortId: string;
  mappingShortId: string;
  recordId: string;
  initialSigners: Signer[];
}

export const useSignersModal = ({
  isOpen,
  userShortId,
  mappingShortId,
  recordId,
  initialSigners,
}: UseSignersModalProps) => {
  const [allSigners, setAllSigners] = useState<Signer[]>([]);
  const [localSigners, setLocalSigners] = useState<Signer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter to get only additional signers (signer_index > 1 and not deactivated)
  const additionalSigners = useMemo(() => {
    return allSigners.filter(
      (signer) => signer.signer_index > 1 && signer.status !== 'deactivated'
    );
  }, [allSigners]);

  // Get primary signer for reference
  const primarySigner = useMemo(() => {
    return allSigners.find((signer) => signer.signer_index === 1);
  }, [allSigners]);

  useEffect(() => {
    if (isOpen && initialSigners) {
      logger.info('useSignersModal', 'Initializing with pre-fetched signers', {
        totalSigners: initialSigners.length,
      });

      setAllSigners(initialSigners);

      // Initialize local signers with additional signers only
      const additionalSignersOnly = initialSigners.filter(
        (signer) => signer.signer_index > 1 && signer.status !== 'deactivated'
      );

      if (additionalSignersOnly.length === 0) {
        // If no additional signers exist, add one empty field by default
        setLocalSigners([
          {
            email: '',
            signer_index: 2, // Start at 2 since 1 is the primary signer
            signer_id: `local-${Date.now()}`,
            status: 'pending_invite',
            role: 'Signer',
            short_id: '',
          },
        ]);
      } else {
        setLocalSigners(additionalSignersOnly);
      }

      logger.info('useSignersModal', 'Successfully initialized signers', {
        totalSigners: initialSigners.length,
        additionalSigners: additionalSignersOnly.length,
        primarySigner: initialSigners.find((s) => s.signer_index === 1)?.email || 'none',
      });

      setIsLoading(false);
    }
  }, [isOpen, initialSigners]);

  const addSigner = () => {
    setLocalSigners((prev) => {
      // Calculate next index based on all signers (including primary)
      const allIndices = [
        ...allSigners.map((s) => s.signer_index),
        ...prev.map((s) => s.signer_index),
      ];
      const nextIndex = Math.max(...allIndices, 1) + 1;

      return [
        ...prev,
        {
          email: '',
          signer_index: nextIndex,
          signer_id: `local-${Date.now()}`, // Temporary local ID
          status: 'pending_invite',
          role: 'Signer',
          short_id: '',
        },
      ];
    });
  };

  const removeSigner = (signerId: string) => {
    setLocalSigners((prev) => prev.filter((s) => s.signer_id !== signerId));
  };

  const updateSignerEmail = (signerId: string, email: string) => {
    setLocalSigners((prev) => prev.map((s) => (s.signer_id === signerId ? { ...s, email } : s)));
  };

  const isSubmissionAllowed = useMemo(() => {
    // All signers must have a valid email
    return localSigners.every((s) => s.email && validateEmail(s.email));
  }, [localSigners]);

  const submitSigners = async () => {
    if (!userShortId || !mappingShortId || !recordId) return false;
    setIsLoading(true);
    try {
      logger.info('useSignersModal', 'Submitting signer changes', {
        localSignersCount: localSigners.length,
        additionalSignersCount: additionalSigners.length,
      });

      const initialSignerMap = new Map(additionalSigners.map((s) => [s.signer_id, s]));
      const localSignerMap = new Map(localSigners.map((s) => [s.signer_id, s]));

      // Step 1: Handle deletes first
      for (const initial of additionalSigners) {
        if (!localSignerMap.has(initial.signer_id)) {
          await signerApi.deleteSigner(userShortId, mappingShortId, recordId, initial.signer_id);
        }
      }

      // Step 2: Handle updates
      for (const local of localSigners) {
        const initial = initialSignerMap.get(local.signer_id);
        if (initial && initial.email !== local.email) {
          await signerApi.updateSigner(
            userShortId,
            mappingShortId,
            recordId,
            local.signer_id,
            local.email
          );
        }
      }

      // Step 3: Handle adds
      for (const local of localSigners) {
        const initial = initialSignerMap.get(local.signer_id);
        if (!initial) {
          await signerApi.addSigner(
            userShortId,
            mappingShortId,
            recordId,
            local.email,
            local.signer_index
          );
        }
      }

      logger.info('useSignersModal', 'Successfully submitted all signer changes');
      return true;
    } catch (error) {
      logger.error('useSignersModal', 'Failed to submit signers', { error });
      throw error; // Re-throw to let component handle
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signers: localSigners,
    allSigners,
    additionalSigners,
    primarySigner,
    addSigner,
    removeSigner,
    updateSignerEmail,
    isSubmissionAllowed,
    submitSigners,
    isLoading,
  };
};
